import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClaimModal = ({ isOpen, onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code || code.trim() === '') {
      toast.error('Please enter a promo code');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/codes/redeem`, {
        code: code.trim()
      });

      if (response.data.success) {
        const { discount_percent, original_price, discounted_price } = response.data;
        toast.success(`${response.data.message}\nOriginal: $${original_price} → Discounted: $${discounted_price.toFixed(2)}`);
        
        // Store code for checkout
        localStorage.setItem('discountCode', code.trim());
        localStorage.setItem('discountPercent', discount_percent);
        localStorage.setItem('discountedPrice', discounted_price);
        
        onSuccess(response.data);
        
        // Redirect to yearly payment with discount applied
        setTimeout(() => {
          onClose();
          // Open Stripe yearly plan link
          // Note: You'll need to create a discounted price in Stripe or use coupons
          window.open(process.env.REACT_APP_STRIPE_YEARLY_LINK, '_blank');
        }, 2000);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Code redemption error:', error);
      toast.error(error.response?.data?.detail || 'Failed to redeem code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            background: 'transparent',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#666',
            lineHeight: '1'
          }}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🎟️</div>
          <h2 style={{ 
            fontSize: '1.8rem', 
            fontWeight: '700', 
            color: '#1A1A1A',
            marginBottom: '8px'
          }}>
            Redeem Your Discount Code
          </h2>
          <p style={{ color: '#666', fontSize: '0.95rem' }}>
            Get 50% OFF on Yearly Pro Plan
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Code Input */}
          <div style={{ marginBottom: '20px' }}>
            <label 
              htmlFor="code"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#1A1A1A',
                fontSize: '0.95rem'
              }}
            >
              Promo Code *
            </label>
            <input
              id="code"
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter code (e.g., LAUNCH50-1)"
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                fontFamily: 'monospace',
                textAlign: 'center',
                outline: 'none',
                transition: 'border-color 0.2s',
                letterSpacing: '1px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {/* Available Codes */}
          <div style={{ 
            background: '#f9fafb', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{ 
              fontSize: '0.85rem', 
              color: '#6b7280', 
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Available Codes:
            </p>
            <p style={{ 
              fontSize: '0.8rem', 
              color: '#9ca3af',
              fontFamily: 'monospace',
              lineHeight: '1.6'
            }}>
              LAUNCH50-1, LAUNCH50-2, LAUNCH50-3<br/>
              LAUNCH50-4, LAUNCH50-5, LAUNCH50-6<br/>
              LAUNCH50-7, LAUNCH50-8, LAUNCH50-9, LAUNCH50-10
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '16px',
              background: isSubmitting 
                ? '#94a3b8' 
                : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
            }}
            onMouseOver={(e) => {
              if (!isSubmitting) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(37,99,235,0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(37,99,235,0.3)';
            }}
          >
            {isSubmitting ? 'Validating...' : '🎟️ Redeem Code & Get 50% OFF'}
          </button>
        </form>

        {/* Footer Note */}
        <p style={{ 
          marginTop: '20px', 
          fontSize: '0.85rem', 
          color: '#6b7280', 
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          Original price: $50/year → Discounted: $25/year<br/>
          You'll be redirected to checkout after code validation.
        </p>
      </div>
    </div>
  );
};

export default ClaimModal;
